import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Package, Plus } from "lucide-react";
import { useState } from "react";
import { ApiError } from "@hostdock/sdk";
import type { PlanRecord } from "@hostdock/types";
import type { PlanInput } from "@hostdock/validation";
import { ActionMenu, Badge, Button, Checkbox, DataTable, Dialog, EmptyState, Field, FormError, Input, PageHeader, Panel, Select, StatusBadge, Textarea, toast } from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";
import { useSession } from "../../session";

export function PlansPage() {
  return (
    <RequirePermission anyOf={["BILLING_MANAGE", "CUSTOMER_VIEW", "WEBSITE_VIEW"]}>
      <Plans />
    </RequirePermission>
  );
}

function Plans() {
  const { can } = useSession();
  const q = useQuery({ queryKey: ["plans"], queryFn: api.plans.list });
  const [editing, setEditing] = useState<PlanRecord | "new" | null>(null);
  return (
    <>
      <PageHeader title="Plans" description="Hosting plans define what a subscription may use. Limits are enforced by the API." actions={can("BILLING_MANAGE") && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setEditing("new")}>New plan</Button>} />
      <Panel flush>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(p) => p.id}
          empty={<EmptyState icon={Package} title="No plans yet" description="Create plans such as Starter, Business and Cloud before assigning subscriptions." />}
          columns={[
            { key: "name", header: "Plan", cell: (p) => <div><p className="font-medium">{p.name} <span className="font-mono text-[12px] text-subtle">{p.slug}</span></p>{p.description && <p className="text-[12px] text-muted">{p.description}</p>}</div> },
            { key: "status", header: "Status", cell: (p) => <StatusBadge status={p.status === "ACTIVE" ? "ACTIVE" : "DISABLED"} /> },
            { key: "sites", header: "Websites", cell: (p) => <span className="tabular-nums">{p.limits.websites}</span> },
            { key: "res", header: "Package resources", cell: (p) => <span className="tabular-nums">{p.limits.cpuCores} CPU · {p.limits.memoryMb} MB · {p.limits.processes} proc.{p.limits.ioMbps ? ` · ${p.limits.ioMbps} MB/s IO` : ""}</span>, hideOnMobile: true },
            { key: "storage", header: "Storage", cell: (p) => <span className="tabular-nums">{p.limits.storageGb} GB</span>, hideOnMobile: true },
            { key: "features", header: "Features", cell: (p) => <div className="flex gap-1">{(["redis", "mongodb", "git"] as const).filter((f) => p.features[f]).map((f) => <Badge key={f}>{f}</Badge>)}</div>, hideOnMobile: true },
            { key: "subs", header: "Subscriptions", cell: (p) => <span className="tabular-nums">{p.subscriptions}</span> },
            { key: "a", header: "", className: "w-10 text-right", cell: (p) => <ActionMenu actions={[{ label: "Edit", onSelect: () => setEditing(p), hidden: !can("BILLING_MANAGE") }]} /> },
          ]}
        />
      </Panel>
      {editing && <PlanEditor plan={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

const LIMIT_FIELDS: [keyof PlanRecord["limits"], string, string?][] = [
  ["websites", "Websites"], ["cpuCores", "CPU for the package", "cores"], ["memoryMb", "RAM for the package", "MB"], ["processes", "Processes for the package"], ["ioMbps", "Disk IO for the package", "MB/s, 0 = unlimited"], ["storageGb", "Storage", "GB"],
  ["inodes", "Inodes"], ["bandwidthGb", "Bandwidth", "GB/month"], ["databases", "Databases"], ["databaseStorageGb", "Database storage", "GB"],
  ["nodeApps", "Node.js apps"], ["pythonApps", "Python apps"], ["cronJobs", "Cron jobs"], ["backupRetentionDays", "Backup retention", "days"], ["sftpUsers", "SFTP users"], ["mailboxes", "Mailboxes"], ["mailboxQuotaMb", "Mailbox size", "MB"],
];

const DEFAULT_PLAN: PlanInput = {
  name: "", slug: "", description: "", status: "ACTIVE",
  limits: { websites: 1, cpuCores: 1, memoryMb: 1024, processes: 512, ioMbps: 0, storageGb: 10, inodes: 250000, bandwidthGb: 100, databases: 2, databaseStorageGb: 2, nodeApps: 0, pythonApps: 0, cronJobs: 5, backupRetentionDays: 7, sftpUsers: 1, mailboxes: 0, mailboxQuotaMb: 1024 },
  features: { redis: false, mongodb: false, git: true },
  runtimes: { php: [], node: [], python: [] },
  regionIds: [],
  placementLabels: {},
};

function PlanEditor({ plan, onClose }: { plan: PlanRecord | null; onClose(): void }) {
  const qc = useQueryClient();
  const regions = useQuery({ queryKey: ["regions"], queryFn: api.regions.list });
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const [f, setF] = useState<PlanInput>(plan ? { name: plan.name, slug: plan.slug, description: plan.description, status: plan.status, limits: plan.limits, features: plan.features, runtimes: plan.runtimes, regionIds: plan.regionIds, placementLabels: plan.placementLabels } : DEFAULT_PLAN);
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => (plan ? api.plans.update(plan.id, f) : api.plans.create(f)),
    onSuccess: () => {
      toast.success(plan ? "Plan updated" : "Plan created");
      void qc.invalidateQueries({ queryKey: ["plans"] });
      onClose();
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const toggleVersion = (rt: "php" | "node" | "python", v: string, on: boolean) =>
    setF({ ...f, runtimes: { ...f.runtimes, [rt]: on ? [...f.runtimes[rt], v] : f.runtimes[rt].filter((x) => x !== v) } });
  const toggleRegion = (id: string, on: boolean) => setF({ ...f, regionIds: on ? [...(f.regionIds ?? []), id] : (f.regionIds ?? []).filter((x) => x !== id) });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      size="lg"
      title={plan ? `Edit ${plan.name}` : "New plan"}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!f.name || !f.slug} onClick={() => save.mutate()}>Save plan</Button></>}
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Name" error={fe.name}>{(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, ...(plan ? {} : { slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") }) })} autoFocus />}</Field>
          <Field label="Slug" error={fe.slug}>{(p) => <Input {...p} value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className="font-mono" />}</Field>
          <Field label="Status">{(p) => <Select {...p} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as "ACTIVE" })}><option value="ACTIVE">Active</option><option value="ARCHIVED">Archived</option></Select>}</Field>
        </div>
        <Field label="Description">{(p) => <Textarea {...p} rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />}</Field>
        <fieldset>
          <legend className="mb-2 text-[11.5px] font-semibold tracking-wider text-subtle uppercase">Limits</legend>
          <p className="mb-3 text-[12.5px] text-muted">CPU, RAM and processes are one pool for the whole package: all of its websites share it on one server, and together they can never use more.</p>
          <div className="grid gap-3 sm:grid-cols-4">
            {LIMIT_FIELDS.map(([k, label, unit]) => (
              <Field key={k} label={unit ? `${label} (${unit})` : label} error={fe[`limits.${k}`]}>
                {(p) => <Input {...p} type="number" step={k === "cpuCores" ? "0.1" : "1"} value={f.limits[k] ?? ""} onChange={(e) => setF({ ...f, limits: { ...f.limits, [k]: Number(e.target.value) } })} />}
              </Field>
            ))}
          </div>
        </fieldset>
        <fieldset className="flex flex-wrap gap-5">
          <legend className="mb-2 text-[11.5px] font-semibold tracking-wider text-subtle uppercase">Features</legend>
          {(["redis", "mongodb", "git"] as const).map((k) => <Checkbox key={k} checked={f.features[k]} onChange={(v) => setF({ ...f, features: { ...f.features, [k]: v } })} label={k === "mongodb" ? "MongoDB" : k === "redis" ? "Redis" : "Git deployments"} />)}
        </fieldset>
        <fieldset>
          <legend className="mb-1 text-[11.5px] font-semibold tracking-wider text-subtle uppercase">Runtime versions</legend>
          <p className="mb-2 text-[12.5px] text-muted">Leave a runtime unticked to allow every maintained version.</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {(["php", "node", "python"] as const).map((rt) => (
              <div key={rt} className="flex flex-col gap-1.5">
                <span className="text-[13px] font-medium">{rt === "php" ? "PHP" : rt === "node" ? "Node.js" : "Python"}</span>
                {settings.data?.runtimes[rt].map((v) => <Checkbox key={v} checked={f.runtimes[rt].includes(v)} onChange={(on) => toggleVersion(rt, v, on)} label={v} />)}
              </div>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="mb-1 text-[11.5px] font-semibold tracking-wider text-subtle uppercase">Regions</legend>
          <p className="mb-2 text-[12.5px] text-muted">Leave all unticked to allow every active region.</p>
          <div className="flex flex-wrap gap-4">{regions.data?.items.map((r) => <Checkbox key={r.id} checked={(f.regionIds ?? []).includes(r.id)} onChange={(on) => toggleRegion(r.id, on)} label={r.name} />)}</div>
        </fieldset>
        {save.error && !Object.keys(fe).length ? <FormError error={save.error} /> : null}
      </div>
    </Dialog>
  );
}
